/**
 * Deals (#766 PR4b): the deal queue, the deal card, its conversation feed and
 * the one-click CRM task. Every answer is checked against the contract in
 * `@aica/api-types` (`deal-queue.ts`).
 *
 * The card opens the deal's CURRENT state (the head of the chain) for any
 * state id, while the task is accepted only for the head: callers send
 * `card.head.stateId` to `createTask`, never the id from a link.
 */
import {
  DEAL_CLIENT_MESSAGE_PROXY_DEADLINE_MS,
  DEAL_CRM_TASK_REFUSALS,
  dealClientMessageResponseSchema,
  dealCardResponseSchema,
  dealConversationsResponseSchema,
  dealCrmTaskResponseSchema,
  dealByCrmEntityResponseSchema,
  dealQueueResponseSchema,
  type DealByCrmEntityResponse,
  type DealCardResponse,
  type DealClientMessageResponse,
  type DealConversationsResponse,
  type DealCrmTaskResponse,
  type DealQueueQuery,
  type DealQueueResponse,
} from '@aica/api-types/schemas';
import { BaseApiClient, type ExpectedRefusal } from '../base';
import { API_ENDPOINTS } from '../endpoints';

/**
 * The 409 refusals of the CRM task: a closed list the deal card explains to
 * the user — answers, not failures (#766). Declared here, by the caller of
 * the endpoint, so the shared fetch layer holds no route of its own (#1751).
 */
const DEAL_TASK_REFUSALS: readonly ExpectedRefusal[] = DEAL_CRM_TASK_REFUSALS.map(
  (code) => ({ status: 409, code })
);

/**
 * The refusals of the client-message draft the card explains in words — the
 * viewer's situation, not a fault: a newer state and a CRM whose processing is
 * closed (409) and the ceilings (429). 503 and 5xx stay unexpected.
 */
const CLIENT_MESSAGE_EXPECTED_REFUSALS: readonly ExpectedRefusal[] = [
  { status: 409, code: 'not_head' },
  { status: 409, code: 'crm_processing_inactive' },
  ...(
    ['over_limit_minute', 'over_limit_hour', 'over_limit_day', 'over_user_limit'] as const
  ).map((code) => ({ status: 429, code })),
];

export class DealsApi extends BaseApiClient {
  async queue(
    params: Partial<Pick<DealQueueQuery, 'status' | 'cursor' | 'includeDemo'>> = {}
  ): Promise<DealQueueResponse> {
    const query = this.buildQuery({
      status: params.status,
      cursor: params.cursor,
      includeDemo: params.includeDemo,
    });
    return this.validatedRequest(
      `${API_ENDPOINTS.DEALS}${query}`,
      dealQueueResponseSchema
    );
  }

  /**
   * The deal state of the CRM card the tab is open on (#745): the freshest head
   * the viewer may see, or `found: false` — indistinguishable from «no state».
   * Embedded only: the installation is the session's, not the request's.
   */
  async byCrmEntity(
    entityType: 'deal' | 'lead',
    entityId: string
  ): Promise<DealByCrmEntityResponse> {
    const query = this.buildQuery({ entityType, entityId });
    return this.validatedRequest(
      `${API_ENDPOINTS.DEALS_BY_CRM_ENTITY}${query}`,
      dealByCrmEntityResponseSchema
    );
  }

  async card(stateId: string): Promise<DealCardResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.DEAL_CARD(stateId),
      dealCardResponseSchema
    );
  }

  async conversations(
    stateId: string,
    before?: string
  ): Promise<DealConversationsResponse> {
    const query = this.buildQuery({ before });
    return this.validatedRequest(
      `${API_ENDPOINTS.DEAL_CONVERSATIONS(stateId)}${query}`,
      dealConversationsResponseSchema
    );
  }

  /** `stateId` — the HEAD (`card.head.stateId`); another id is a 409 `not_head`. */
  async createTask(stateId: string): Promise<DealCrmTaskResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.DEAL_CRM_TASK(stateId),
      dealCrmTaskResponseSchema,
      {
        method: 'POST',
        expectedRefusals: DEAL_TASK_REFUSALS,
      }
    );
  }

  /**
   * A draft message to the client by the deal's HEAD state (#1675). A paid
   * model call: no retries (a second call would be a second charge), and the
   * wait is 10 s longer than edge-api waits for workflows-api, so the client
   * reads its "too long" answer instead of cutting the request itself.
   */
  async draftClientMessage(stateId: string): Promise<DealClientMessageResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.DEAL_CLIENT_MESSAGE(stateId),
      dealClientMessageResponseSchema,
      {
        method: 'POST',
        signal: AbortSignal.timeout(DEAL_CLIENT_MESSAGE_PROXY_DEADLINE_MS + 10_000),
        expectedRefusals: CLIENT_MESSAGE_EXPECTED_REFUSALS,
      },
      0
    );
  }
}
